import PageBreadcrumb from "../../components/common/CustomBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import TextArea from "../../components/form/input/TextArea";
import Radio from "../../components/form/input/Radio";
import { useState, useRef } from "react";
import useGoBack from "../../hooks/useGoBack";
import FlashcardService from "../../services/flashcard.service";
import { toast } from "react-toastify";
import { uuidv4 } from "@/utils/uuid";
import ImageCropModal from "./ImageCropModal";
import { getImageDimensions } from "@/utils/validation";
import { base64ToFile, resizeImageToFitMinimumSize } from "@/utils/reSizeImage";
import { AIIcon } from "@/icons";
import AIService from "@/services/ai.service";
import JSZip from 'jszip';
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

const ChoiceAnswer = ["A", "B", "C", "D"];

export default function CreateFlashcard() {
  const { t } = useTranslation();
  const { deck_id } = useParams();
  const [image, setImage] = useState<string>("");
  const [sound, setSound] = useState<string>("");
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [soundUploadProgress, setSoundUploadProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const soundInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [flashcardData, setFlashcardData]: any = useState({
    title_FC: "",
    answer: "",
    image: "",
    sound: "",
    definition: "",
    moreQuestions: [],
  });

  const [errors, setErrors]: any = useState({
    title_FC: "",
    answer: "",
    image: "",
    sound: "",
    definition: "",
    moreQuestions: [],
  });

  const [selectedUrlOrUpload, setSelectedUrlOrUpload] = useState({
    image: "Upload",
    sound: "Upload",
  });
  const [showCropModal, setShowCropModal] = useState(false);
  const [tempImage, setTempImage] = useState<any>("");
  const [isLoadingGenerate, setIsLoadingGenerate] = useState(false);
  const [imageBlobs, setImageBlobs] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [aiProvider, setAiProvider] = useState<string>(() => {
    return localStorage.getItem("aiProvider") || "gemini";
  });
  const goBack = useGoBack();

  const validate = () => {
    const newErrors: any = {
      title_FC: "",
      answer: "",
      image: "",
      sound: "",
      definition: "",
      moreQuestions: [],
    };

    if (flashcardData.title_FC.trim() === "") {
      newErrors.title_FC = t("Question is required");
    }
    if (flashcardData.answer.trim() === "") {
      newErrors.answer = t("Answer is required");
    }
    if (flashcardData.definition.trim() === "") {
      newErrors.definition = t("Definition is required");
    }

    if (flashcardData.title_FC.split("\n").length > 3) {
      newErrors.title_FC = t("You can only add line breaks 3 times");
    }

    if (flashcardData.answer.split("\n").length > 3) {
      newErrors.answer = t("You can only add line breaks 3 times");
    }

    if (flashcardData.definition.split("\n").length > 3) {
      newErrors.definition = t("You can only add line breaks 3 times");
    }

    if (flashcardData.moreQuestions && flashcardData.moreQuestions.length > 0) {
      newErrors.moreQuestions = flashcardData.moreQuestions.map((question: any) => {
        const questionErrors: any = {};
        questionErrors.id = question.id;
        questionErrors.question = "";
        questionErrors.answer = question.type === "isYesNo" ? ["", ""] : ["", "", "", ""];
        questionErrors.type = "";
        questionErrors.correctAnswer = "";
        if (!question.question || question.question.trim() === "") {
          questionErrors.question = t("Question is required");
        }
        if (question.question.split("\n").length > 3) {
          questionErrors.question = t("You can only add line breaks 3 times");
        }
        if (!question.type) {
          questionErrors.type = t("Type is required");
        }
        let count = 0;
        for (const ans of question.answer) {
          if (!ans || ans.trim() === "") {
            questionErrors.answer[count] = t("Answer is required");
          }
          if (ans.split("\n").length > 3) {
            questionErrors.answer[count] = t("You can only add line breaks 3 times");
          }
          count++;
        }
        if (!question.correctAnswer || question.correctAnswer.trim() === "") {
          questionErrors.correctAnswer = t("Correct answer is required");
        }
        return questionErrors;
      });
    }

    const hasMainErrors = Object.keys(newErrors).some(
      key => key !== 'moreQuestions' && newErrors[key] !== ""
    );

    const hasQuestionErrors = newErrors.moreQuestions.some((question: any) => {
      if (question.question !== "" || question.type !== "" || question.correctAnswer !== "") return true;

      if (typeof question.answer === "string") {
        return question.answer !== "";
      } else {
        return question.answer.some((ans: string) => ans !== "");
      }
    });

    setErrors(newErrors);
    return hasMainErrors || hasQuestionErrors;
  };



  const handleRadioChangeImage = (value: string) => {
    setSelectedUrlOrUpload({
      ...selectedUrlOrUpload,
      image: value
    });
    setImage("");
    setFlashcardData({
      ...flashcardData,
      image: "",
    })
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRadioChangeSound = (value: string) => {
    setSelectedUrlOrUpload({
      ...selectedUrlOrUpload,
      sound: value
    });
    setSound("");
    setFlashcardData({
      ...flashcardData,
      sound: "",
    })
    if (soundInputRef.current) {
      soundInputRef.current.value = "";
    }
  };

  const handleCreateFlashcard = () => {
    setIsLoading(true);
    const fd = new FormData();
    for (let key of Object.keys(flashcardData)) {
      if (key === "moreQuestions") {
        let moreQuestions = flashcardData[key];
        for (let it of moreQuestions) {
          if (ChoiceAnswer.indexOf(it.correctAnswer) >= 0) {
            it.correctAnswer = it.answer[ChoiceAnswer.indexOf(it.correctAnswer)]
          }
        }
        fd.append(key, JSON.stringify(moreQuestions));
      } else {
        fd.append(key, flashcardData[key]);
      }
    }
    fd.append("deck_id", deck_id || "");
    FlashcardService.createDeckFlashcardAdmin(fd)
      .then((data: any) => {
        if (data.data.status === 200) {
          toast.success(t("Create Successfully!"));
          setTimeout(() => {
            window.location.href = "/list-flashcards/" + deck_id;
          }, 1500);
        } else {
          toast.error(t("Create Failed!"));
          setIsLoading(false);
        }
      })
      .catch((errors) => {
        setIsLoading(false);
        toast.error(t("Create Failed!"));
      });
  };

  const simulateProgress = (setProgress: (progress: number) => void, onComplete: () => void) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 5;
      if (progress >= 100) {
        clearInterval(interval);
        onComplete();
      } else {
        setProgress(progress);
      }
    }, 50);
  };

  const handleAddMoreQuestion = () => {
    const currentQuestions = flashcardData.moreQuestions || [];
    const lastId = currentQuestions.length > 0 ? currentQuestions[currentQuestions.length - 1].id : 0;
    const newQuestion = {
      id: lastId + 1,
      type: "isYesNo",
      question: "",
      answer: ["Yes", "No"],
      correctAnswer: ""
    };

    const updatedQuestions = [...currentQuestions, newQuestion];
    setFlashcardData({
      ...flashcardData,
      moreQuestions: updatedQuestions
    });
  };

  const handleRemoveAnswer = (questionData: any, answerIndex: number) => {
    const updatedQuestions = [...flashcardData.moreQuestions];

    const questionIndex = updatedQuestions.findIndex(q => q.id === questionData.id);

    if (questionIndex !== -1) {
      const updatedQuestion = {
        ...updatedQuestions[questionIndex],
        answer: updatedQuestions[questionIndex].answer.filter((_: any, idx: any) => idx !== answerIndex)
      };

      if (updatedQuestions[questionIndex].correctAnswer === ChoiceAnswer[answerIndex]) {
        updatedQuestion.correctAnswer = "";
      }

      updatedQuestions[questionIndex] = updatedQuestion;

      setFlashcardData({
        ...flashcardData,
        moreQuestions: updatedQuestions
      });
    }
  };

  const handleAddMoreAnswer = (questionData: any) => {
    const updatedQuestions = [...flashcardData.moreQuestions];

    const questionIndex = updatedQuestions.findIndex(q => q.id === questionData.id);

    if (questionIndex !== -1) {
      const updatedQuestion = {
        ...updatedQuestions[questionIndex],
        answer: [...updatedQuestions[questionIndex].answer, ""]
      };

      updatedQuestions[questionIndex] = updatedQuestion;

      setFlashcardData({
        ...flashcardData,
        moreQuestions: updatedQuestions
      });
    }
  };

  const handleRemoveQuestion = (id: any) => {
    const updatedQuestions = flashcardData.moreQuestions.filter((q: any) => q.id !== id);
    setFlashcardData({
      ...flashcardData,
      moreQuestions: updatedQuestions
    });
  };

  const handleQuestionChange = (id: number, type: string, value: any) => {
    let updateData: any = { [type]: value }
    if (type === "type") {
      if (value === "isYesNo") {
        updateData = { [type]: value, answer: ["Yes", "No"], correctAnswer: "" }
        const updatedQuestions = errors.moreQuestions.map((q: any) =>
          q.id === id ? { ...q, answer: ["Yes", "No"], correctAnswer: "" } : q
        );
        setErrors({
          ...errors,
          moreQuestions: updatedQuestions
        })
      }
      if (value === "isMultiple") {
        updateData = { [type]: value, answer: ["", "", "", ""], correctAnswer: "" }
        const updatedQuestions = errors.moreQuestions.map((q: any) =>
          q.id === id ? { ...q, answer: ["", "", "", ""], correctAnswer: "" } : q
        );
        setErrors({
          ...errors,
          moreQuestions: updatedQuestions
        })
      }
    }
    const updatedQuestions = flashcardData.moreQuestions.map((q: any) =>
      q.id === id ? { ...q, ...updateData } : q
    );
    setFlashcardData({
      ...flashcardData,
      moreQuestions: updatedQuestions
    });
  };
  //
  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const handleGenerateImage = async () => {
    const newErrors: any = {
      title_FC: "",
      answer: "",
      image: "",
      sound: "",
      definition: "",
      moreQuestions: [],
    };

    if (flashcardData.title_FC.trim() === "") {
      newErrors.title_FC = t("Question is required");
    }
    if (flashcardData.answer.trim() === "") {
      newErrors.answer = t("Answer is required");
    }

    const hasMainErrors = Object.keys(newErrors).some(
      key => key !== 'moreQuestions' && newErrors[key] !== ""
    );
    setErrors(newErrors);
    if (hasMainErrors) {
      return;
    }
    try {
      setIsLoadingGenerate(true);
      const res = await AIService.generateFlashcardImage({
        question: flashcardData.title_FC,
        answer: flashcardData.answer,
      }, aiProvider);
      
      const imagesList = res.data.images || [];
      if (imagesList.length !== 0) {
        setTempImage(imagesList[0]);
        setImageBlobs(imagesList);
      }
      setShowCropModal(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingGenerate(false);
    }
  }

  return (
    <div>
      <PageMeta
        title="VIYUME - VIYUME Dashboard"
        description=""
      />
      <div className="mx-auto max-w-[80%] grid grid-cols-1 gap-6">
        <div className="space-y-6">
          <ComponentCard title="">
            <div className="space-y-6">
              <div>
                <Label>{t('Question')}</Label>
                <TextArea
                  value={flashcardData.title_FC}
                  onChange={(value) => setFlashcardData({ ...flashcardData, title_FC: value })}
                  rows={6}
                  placeholder={`${t('Please enter question, you can only add line breaks 3 times')}`}
                />
                {errors.title_FC && (
                  <p className="mt-1 text-sm text-red-600">{errors.title_FC}</p>
                )}
              </div>
              <div>
                <Label>{t('Answer')}</Label>
                <TextArea
                  value={flashcardData.answer}
                  onChange={(value) => setFlashcardData({ ...flashcardData, answer: value })}
                  rows={6}
                  placeholder={`${t('Please enter answer, you can only add line breaks 3 times')}`}
                />
                {errors.answer && (
                  <p className="mt-1 text-sm text-red-600">{errors.answer}</p>
                )}
              </div>
              <div>
                <Label>{t('Definition')}</Label>
                <TextArea
                  value={flashcardData.definition}
                  onChange={(value) => setFlashcardData({ ...flashcardData, definition: value })}
                  rows={3}
                  placeholder={`${t('Please enter definition, you can only add line breaks 3 times')}`}
                />
                {errors.definition && (
                  <p className="mt-1 text-sm text-red-600">{errors.definition}</p>
                )}
              </div>
              <div>
                <Label>{t("Image (Optional)")}</Label>
                <div className="flex flex-wrap items-center gap-8">
                  <Radio
                    id="radio3"
                    name="choiceImage"
                    value="URL"
                    checked={selectedUrlOrUpload.image === "URL"}
                    onChange={handleRadioChangeImage}
                    label="URL"
                  />
                  <Radio
                    id="radio4"
                    name="choiceImage"
                    value="Upload"
                    checked={selectedUrlOrUpload.image === "Upload"}
                    onChange={handleRadioChangeImage}
                    label="Upload"
                  />
                </div>
                {(selectedUrlOrUpload.image === 'Upload') && (
                  <>
                    {showCropModal && (
                      <ImageCropModal
                        image={tempImage}
                        images={imageBlobs}
                        onCropComplete={(croppedImage: any) => {
                          if (typeof croppedImage === 'string') {
                            setImage(croppedImage);
                            setFlashcardData({
                              ...flashcardData,
                              image: base64ToFile(croppedImage, `${new Date().getTime()}.jpg`),
                            });
                          } else if (croppedImage?.type === 'cached') {
                            setImage(croppedImage.url);
                            setFlashcardData({
                              ...flashcardData,
                              image: croppedImage.key,
                            });
                            // Handle file input reset if it was set
                            if (fileInputRef.current) {
                               fileInputRef.current.value = "";
                            }
                          }
                          setShowCropModal(false);
                        }}
                        onClose={() => {
                          setTempImage("");
                          setShowCropModal(false);
                        }}
                        onCancel={() => {
                          setTempImage("");
                          setImage("");
                          setFlashcardData({
                            ...flashcardData,
                            image: "",
                          });
                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                          }
                          setShowCropModal(false);
                        }}
                      />
                    )}
                  </>)}
                {selectedUrlOrUpload.image === "Upload" ?
                  <>
                    <div className="flex flex-col space-y-2 mt-5 max-w-xs">
                      <Label>{t("AI Image Provider")}</Label>
                      <div className="flex flex-wrap items-center gap-4 mb-2">
                        <Radio
                          id="provider-gemini"
                          name="aiProvider"
                          value="gemini"
                          checked={aiProvider === "gemini"}
                          onChange={(val) => {
                            setAiProvider(val);
                            localStorage.setItem("aiProvider", val);
                          }}
                          label="Gemini"
                        />
                        <Radio
                          id="provider-openai"
                          name="aiProvider"
                          value="openai"
                          checked={aiProvider === "openai"}
                          onChange={(val) => {
                            setAiProvider(val);
                            localStorage.setItem("aiProvider", val);
                          }}
                          label="OpenAI"
                        />
                      </div>
                      <button
                        onClick={handleGenerateImage}
                        disabled={isLoadingGenerate}
                        className="flex items-center justify-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        {isLoadingGenerate ? (
                          <>
                            <svg className="w-5 h-5 animate-spin mr-2" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            {t("Generating")}...
                          </>
                        ) : (
                          <div className="flex items-center justify-center">
                            <img
                              src={AIIcon}
                              alt="AI Icon"
                              className="w-5 h-5 object-contain filter invert-[29%] sepia-[98%] saturate-[7500%] hue-rotate-[245deg] brightness-[90%] contrast-[90%]"
                            />
                            {t("Generate Image with AI")}
                          </div>
                        )}
                      </button>
                      <div className="relative">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
                              if (!validImageTypes.includes(file.type)) {
                                setErrors((prev: any) => ({
                                  ...prev,
                                  image: 'Please select a valid image file (JPEG, PNG, GIF, WEBP)'
                                }));
                                setImage("");
                                setFlashcardData({
                                  ...flashcardData,
                                  image: "",
                                })
                                if (fileInputRef.current) {
                                  fileInputRef.current.value = "";
                                }
                                return;
                              }

                              try {
                                const dimensions: any = await getImageDimensions(file);
                                if (dimensions) {
                                  if (dimensions.width < 800 || dimensions.width > 1200 || dimensions.height < 800 || dimensions.height > 1200) {
                                    const resizedFile: any = await resizeImageToFitMinimumSize(file, 400, 400);
                                    setErrors((prev: any) => ({
                                      ...prev,
                                      image: ""
                                    }));
                                    setUploadProgress(0);
                                    const reader = new FileReader();

                                    simulateProgress(setUploadProgress, () => {
                                      reader.readAsDataURL(resizedFile);
                                    });
                                    reader.onload = (event) => {
                                      if (event.target?.result) {
                                        const base64String = event.target.result.toString();
                                        setUploadProgress(100);
                                        setTempImage(base64String);
                                        setTimeout(() => {
                                          setUploadProgress(0);
                                          setShowCropModal(true);
                                        }, 1000);
                                      }
                                    };
                                    reader.onerror = () => {
                                      toast.error('Error reading file');
                                      setUploadProgress(0);
                                    };
                                    return
                                  }
                                }
                              } catch (error) {
                                setErrors((prev: any) => ({
                                  ...prev,
                                  image: 'Failed to read image dimensions'
                                }));
                                setImage("");
                                setFlashcardData({
                                  ...flashcardData,
                                  image: "",
                                })
                                if (fileInputRef.current) {
                                  fileInputRef.current.value = "";
                                }
                                return;
                              }

                              setErrors((prev: any) => ({
                                ...prev,
                                image: ""
                              }));
                              setUploadProgress(0);
                              const reader = new FileReader();

                              simulateProgress(setUploadProgress, () => {
                                reader.readAsDataURL(file);
                              });
                              reader.onload = (event) => {
                                if (event.target?.result) {
                                  const base64String = event.target.result.toString();
                                  setUploadProgress(100);
                                  setTempImage(base64String);
                                  setTimeout(() => {
                                    setUploadProgress(0);
                                    setShowCropModal(true);
                                  }, 1000);
                                }
                              };
                              reader.onerror = () => {
                                toast.error('Error reading file');
                                setUploadProgress(0);
                              };
                            }
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          id="file-upload"
                        />
                        <label
                          htmlFor="file-upload"
                          className="flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg transition-colors cursor-pointer font-medium"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                          {t("Upload Image")}

                        </label>
                      </div>
                    </div>



                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                          <div
                            className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{uploadProgress}% uploaded</p>
                      </div>
                    )}

                    {image && (
                      <div className="mt-4 relative inline-flex items-center gap-2">
                        <img
                          src={image}
                          alt="Preview"
                          className="max-w-xs rounded-lg shadow-lg"
                          onClick={() => setIsModalOpen(true)}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImage("");
                            setFlashcardData({
                              ...flashcardData,
                              image: "",
                            })
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                    {errors.image && (
                      <p className="mt-1 text-sm text-red-500">{errors.image}</p>
                    )}
                  </> :
                  <>
                    <Input
                      type="text"
                      value={flashcardData.image}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFlashcardData({ ...flashcardData, image: e.target.value })
                        setImage(e.target.value)
                      }}
                      placeholder={t("Please enter image URL")}

                      className="mt-2"
                    />
                    {image && (
                      <div className="mt-4 relative inline-flex items-center gap-2">
                        <img
                          src={image}
                          alt="Preview"
                          className="max-w-xs rounded-lg shadow-lg"
                          onClick={() => setIsModalOpen(true)}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImage("");
                            setFlashcardData({
                              ...flashcardData,
                              image: "",
                            })
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                }

              </div>
              <div>
                <Label>{t('Sound (Optional)')}</Label>
                <div className="flex flex-wrap items-center gap-8">
                  <Radio
                    id="radio5"
                    name="choiceSound"
                    value="URL"
                    checked={selectedUrlOrUpload.sound === "URL"}
                    onChange={handleRadioChangeSound}
                    label="URL"
                  />
                  <Radio
                    id="radio6"
                    name="choiceSound"
                    value="Upload"
                    checked={selectedUrlOrUpload.sound === "Upload"}
                    onChange={handleRadioChangeSound}
                    label="Upload"
                  />
                </div>
                {selectedUrlOrUpload.sound === "Upload" ?
                  <>
                    <div className="flex flex-col space-y-2 mt-5 max-w-xs">
                      <div className="relative">
                        <input
                          ref={soundInputRef}
                          type="file"
                          accept="audio/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const validAudioTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp3'];
                              if (!validAudioTypes.includes(file.type)) {
                                setErrors((prev: any) => ({
                                  ...prev,
                                  sound: 'Please select a valid audio file (MP3, WAV, OGG)'
                                }));
                                setSound("");
                                setFlashcardData({
                                  ...flashcardData,
                                  sound: "",
                                })
                                if (soundInputRef.current) {
                                  soundInputRef.current.value = "";
                                }
                                return;
                              }

                              if (file.size > 25 * 1024 * 1024) {
                                setErrors((prev: any) => ({
                                  ...prev,
                                  sound: 'File size must be less than 25MB'
                                }));
                                setSound("");
                                setFlashcardData({
                                  ...flashcardData,
                                  sound: "",
                                })
                                if (soundInputRef.current) {
                                  soundInputRef.current.value = "";
                                }
                                return;
                              }

                              // Clear sound error when valid file is selected
                              setErrors((prev: any) => ({
                                ...prev,
                                sound: ""
                              }));

                              setSoundUploadProgress(0);
                              const reader = new FileReader();

                              simulateProgress(setSoundUploadProgress, () => {
                                reader.readAsDataURL(file);
                              });

                              reader.onload = (event) => {
                                if (event.target?.result) {
                                  const base64String = event.target.result.toString();
                                  setSoundUploadProgress(100);
                                  setTimeout(() => {
                                    setSoundUploadProgress(0);
                                    setSound(base64String);
                                    setFlashcardData({
                                      ...flashcardData,
                                      sound: file,
                                    })
                                  }, 1000);
                                }
                              };

                              reader.onerror = () => {
                                toast.error('Error reading file');
                                setSoundUploadProgress(0);
                              };

                              reader.readAsDataURL(file);
                            }
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          id="sound-upload"
                        />
                        <label
                          htmlFor="sound-upload"
                          className="flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg transition-colors cursor-pointer font-medium"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                          {t('Upload Sound')}
                        </label>
                      </div>
                    </div>


                    {soundUploadProgress > 0 && soundUploadProgress < 100 && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                          <div
                            className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                            style={{ width: `${soundUploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{soundUploadProgress}% uploaded</p>
                      </div>
                    )}

                    {sound && (
                      <div className="mt-4 flex items-center gap-2">
                        <audio controls>
                          <source src={sound} type="audio/mpeg" />
                          Your browser does not support the audio element.
                        </audio>
                        <button
                          type="button"
                          onClick={() => {
                            setSound("");
                            setFlashcardData({
                              ...flashcardData,
                              sound: "",
                            })
                            if (soundInputRef.current) {
                              soundInputRef.current.value = "";
                            }
                          }}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                    {errors.sound && (
                      <p className="mt-1 text-sm text-red-500">{errors.sound}</p>
                    )}
                  </> :
                  <>
                    <Input
                      type="text"
                      value={flashcardData.sound}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFlashcardData({ ...flashcardData, sound: e.target.value })
                        setSound(e.target.value)
                      }}
                      placeholder="Please enter sound URL"
                      className="mt-2"
                    />
                    {sound && (
                      <div className="mt-4 flex items-center gap-2">
                        <audio controls>
                          <source src={sound} type="audio/mpeg" />
                          Your browser does not support the audio element.
                        </audio>
                        <button
                          type="button"
                          onClick={() => {
                            setSound("");
                            setFlashcardData({
                              ...flashcardData,
                              sound: "",
                            })
                            if (soundInputRef.current) {
                              soundInputRef.current.value = "";
                            }
                          }}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                }
              </div>
            </div>
            {flashcardData.moreQuestions.length !== 0 && <hr className="my-6 border-gray-300 dark:border-gray-700" />}
            {flashcardData.moreQuestions.map((dt: any, i: number) => (
              <div key={dt.id}>
                <Label className="font-bold text-xl mt-4 mb-2">{t('Question')} {i + 1}</Label>
                <Label>{t('Type')}</Label>
                <div className="flex flex-wrap items-center gap-8">
                  <Radio
                    id={uuidv4()}
                    name={`choiceTypeQuestion-${dt.id}`}
                    value="isYesNo"
                    checked={dt.type === "isYesNo"}
                    onChange={() => handleQuestionChange(dt.id, "type", "isYesNo")}
                    label="True / False"
                  />
                  <Radio
                    id={uuidv4()}
                    name={`choiceTypeQuestion-${dt.id}`}
                    value="isMultiple"
                    checked={dt.type === "isMultiple"}
                    onChange={() => handleQuestionChange(dt.id, "type", "isMultiple")}
                    label={t('Multiple')}
                  />
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-red-400 shadow-sm text-sm font-medium rounded-md text-red-800 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    onClick={() => handleRemoveQuestion(dt.id)}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 3h4a1 1 0 011 1v1H9V4a1 1 0 011-1z" />
                    </svg>
                    {t('Remove')}
                  </button>
                </div>

                <div className="mt-3">
                  <Label>{t('Question')}</Label>
                  <TextArea
                    value={dt.question}
                    onChange={(value) => handleQuestionChange(dt.id, "question", value)}
                    rows={6}
                    placeholder={`${t('Please enter question, you can only add line breaks 3 times')}`}
                  />
                  {errors.moreQuestions.find((g: any) => dt.id === g.id) && (
                    <p className="mt-1 text-sm text-red-600">{errors.moreQuestions.find((g: any) => dt.id === g.id)?.question}</p>
                  )}
                </div>
                {dt.answer.map((ans: string, ind: number) => {
                  return (
                    <div className="mt-3" key={`${dt.id}-${ind}`}>
                      {dt.type === "isMultiple" ?
                        <Label>{t('Answer')} {ChoiceAnswer[ind]}</Label> :
                        <div className="flex items-center justify-between">
                          <Label>{t('Answer')} {ind + 1}</Label>
                          {dt.answer.length > 2 && (
                            <button
                              type="button"
                              className="inline-flex items-center px-3 py-1 border border-red-400 shadow-sm text-xs font-medium rounded-md text-red-800 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              onClick={() => handleRemoveAnswer(dt, ind)}
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 3h4a1 1 0 011 1v1H9V4a1 1 0 011-1z" />
                              </svg>
                              {t('Remove')}
                            </button>
                          )}
                        </div>
                      }
                      <Radio
                        id={uuidv4()}
                        name={`choiceCorrectAnswer-${dt.id}-${ind}`}
                        value={ChoiceAnswer[ind]}
                        checked={dt.correctAnswer === ChoiceAnswer[ind]}
                        onChange={() => handleQuestionChange(dt.id, "correctAnswer", ChoiceAnswer[ind])}
                        label={t('Correct Answer')}
                        className="mb-2"
                      />
                      <TextArea
                        value={ans}
                        onChange={(value) => {
                          const updatedAnswers = [...dt.answer];
                          updatedAnswers[ind] = value;
                          handleQuestionChange(dt.id, "answer", updatedAnswers);
                        }}
                        rows={6}
                        placeholder={`${t('Please enter answer, you can only add line breaks 3 times')}`}
                        disabled={dt.type !== "isMultiple"}
                      />
                      {errors.moreQuestions.find((g: any) => dt.id === g.id) && (
                        <p className="mt-1 text-sm text-red-600">{errors.moreQuestions.find((g: any) => dt.id === g.id)?.answer[ind]}</p>
                      )}
                    </div>
                  )
                })}
                {(errors.moreQuestions.find((g: any) => dt.id === g.id) && errors.moreQuestions.find((g: any) => dt.id === g.id))?.answer.every((val: any) => val === "") && (
                  <p className="mt-1 text-sm text-red-600">{errors.moreQuestions.find((g: any) => dt.id === g.id)?.correctAnswer}</p>
                )}
                {dt.type === "isYesNo" &&
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="mt-3 inline-flex items-center px-4 py-2 border border-blue-400 shadow-sm text-sm font-medium rounded-md text-blue-800 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={() => handleAddMoreAnswer(dt)}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      {t('Add Answer')}
                    </button>
                  </div>
                }
                {(flashcardData.moreQuestions.length !== 1 && flashcardData.moreQuestions.length - 1 !== i) && (
                  <hr className="my-6 border-gray-300 dark:border-gray-700" />
                )}
              </div>
            ))}
            <hr className="my-6 border-gray-300 dark:border-gray-700" />
            <div className="flex justify-between items-center">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-yellow-400 shadow-sm text-sm font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                onClick={handleAddMoreQuestion}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                {t('Add Question')}
              </button>

              <div className="flex">
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={goBack}
                >
                  {t('Cancel')}
                </button>
                <div className="mx-2"></div>
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => {
                    if (!isLoading) {
                      const hasErrors = validate();
                      if (!hasErrors) {
                        handleCreateFlashcard()
                      } else {
                        toast.warning("Validation failed - Please check your inputs");
                      }
                    }
                  }}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('Creating')}...
                    </div>
                  ) : (
                    <>
                      {t('Create')}
                    </>
                  )}
                </button>
              </div>
            </div>
            {isModalOpen && (
              <>
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-70">
                  <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl p-4 relative m-4">
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="absolute -top-2 -right-2 z-50 bg-gray-800 text-white hover:bg-gray-900 w-8 h-8 rounded-full flex items-center justify-center text-lg transition-colors"
                    >
                      &times;
                    </button>
                    <div className="h-[80vh] flex items-center justify-center">
                      <img
                        src={image}
                        alt="Preview"
                        className="max-h-full max-w-full object-contain rounded-md"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </ComponentCard>
        </div>
      </div>
    </div>
  );
}

